import { useEffect, useState } from 'react'
import { Link, useBlocker, useNavigate, useSearchParams } from 'react-router-dom'
import { ShowPackage } from '@schemas'
import { useCurrentUser } from '@/api/hooks'
import { useQuery } from '@tanstack/react-query'
import { liveApiClient } from '@/api/live-query'
import {
  downloadJson,
  transferRequest,
  useInstallationIdentity,
  useOwnership,
  useTransferAction,
} from '@/api/show-transfer'
import { useCurrentShow } from '@/context/current-show'
import { Stepper } from '@/components/stepper/Stepper'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Check, JsonFile } from '@/components/show-transfer/files'
import {
  PackageReviewWorkspace,
  PackageSummary,
} from '@/components/show-transfer/PackageReviewWorkspace'
import {
  usePackageReview,
  type ReviewController,
} from '@/components/show-transfer/usePackageReview'
import {
  StatusNotice,
  WorkflowFooter,
  WorkflowHeading,
  WorkflowLayout,
} from '@/components/show-transfer/WorkflowLayout'

export default function ShowImportPage() {
  const user = useCurrentUser()
  if (user.isLoading) return <p role="status">Loading administrator access…</p>
  if (!user.data?.isAdmin)
    return <StatusNotice tone="error">Administrator access is required.</StatusNotice>
  return <ImportWizard key={user.data.id} />
}
function ImportWizard() {
  const identity = useInstallationIdentity()
  const [params] = useSearchParams()
  const showQueryParam = params.get('showId')
    ? `showId=${encodeURIComponent(params.get('showId')!)}`
    : ''
  const transferHome = `/admin/show-transfer${showQueryParam ? `?${showQueryParam}` : ''}`
  const [file, setFile] = useState<{ value: unknown; name: string }>()
  const [replacement, setReplacement] = useState<typeof file>()
  const [reading, setReading] = useState(false)
  const [step, setStep] = useState('choose')
  const c = usePackageReview({
    packageValue: file?.value,
    installationKey: identity.data?.installationKey ?? '',
    disabled: reading || !identity.data,
  })
  const blocker = useBlocker(c.locked || reading)
  useEffect(() => {
    if (c.result) setStep('done')
  }, [c.result])
  const hasDecisions =
    c.choices.mappings.length +
      c.choices.priorClasses.length +
      c.choices.resolutions.length +
      c.choices.clubAffiliations.length +
      c.baselines.length >
    0
  function replace(next: NonNullable<typeof file>) {
    c.reset()
    setFile(next)
    setReplacement(undefined)
    setStep('choose')
  }
  const choose = (
    <div className="space-y-6">
      <WorkflowHeading description="Use a JSON file exported from Honey Show. Its organization, classes and supporting records are included.">
        Choose a show file
      </WorkflowHeading>
      <JsonFile
        label={file ? 'Change show file' : 'Choose show file'}
        dropzone
        disabled={c.locked || reading}
        onReading={setReading}
        onLoad={(value, name) => {
          ShowPackage.parse(value)
          const next = { value, name: name ?? 'Show package' }
          if (file && hasDecisions) setReplacement(next)
          else replace(next)
        }}
      />
      <Dialog
        open={!!replacement}
        onOpenChange={open => {
          if (!open) setReplacement(undefined)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace this file?</DialogTitle>
            <DialogDescription>
              Replacing the file discards its saved decisions and correction baselines.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplacement(undefined)}>
              Keep current file
            </Button>
            <Button variant="destructive" onClick={() => replacement && replace(replacement)}>
              Discard decisions and replace file
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {file && (
        <>
          <p className="break-all text-sm text-muted-foreground">Selected file: {file.name}</p>
          <PackageSummary value={file.value} />
        </>
      )}
      <p className="text-sm text-muted-foreground">
        Nothing is imported until you review and confirm.
      </p>
      {identity.isError && (
        <StatusNotice tone="error" title="This server is not ready">
          <p>Unable to load this installation identity.</p>
          <Button variant="outline" onClick={() => void identity.refetch()}>
            Retry installation identity
          </Button>
        </StatusNotice>
      )}
      <WorkflowFooter
        secondary={
          <Button variant="outline" asChild>
            <Link to={transferHome}>Cancel</Link>
          </Button>
        }
      >
        <Button
          disabled={!file || !identity.data || reading || c.locked || !!replacement}
          onClick={() => {
            setStep('review')
            void c.preview(c.choices, c.baselines, true)
          }}
        >
          Review file
        </Button>
      </WorkflowFooter>
      {file && (
        <details open={params.has('recover') || undefined}>
          <summary>Resume a saved review</summary>
          <div className="mt-3">
            <JsonFile
              label="Load review recovery file"
              onReading={c.setReading}
              disabled={reading || c.locked || !identity.data}
              onLoad={value => {
                c.recover(value)
                setStep('review')
              }}
            />
          </div>
        </details>
      )}
      {!file && params.has('recover') && (
        <StatusNotice tone="warning">
          Choose your original show file, then load the saved review recovery file.
        </StatusNotice>
      )}
      <Link
        className="inline-block text-sm underline underline-offset-4"
        to={`/admin/show-transfer?task=received${showQueryParam ? `&${showQueryParam}` : ''}`}
      >
        Review received deliveries
      </Link>
    </div>
  )
  const review = (
    <div className="space-y-6">
      <WorkflowHeading description="Check the changes and resolve any decisions before importing.">
        {c.uncertain ? 'Check your import result' : 'Review changes'}
      </WorkflowHeading>
      {file && <PackageSummary value={file.value} />}
      <PackageReviewWorkspace controller={c} wizard onBack={() => setStep('choose')} />
    </div>
  )
  return (
    <WorkflowLayout
      title="Import a show"
      backTo={transferHome}
      description="Bring in a show file and review its changes before importing."
    >
      {blocker.state === 'blocked' && (
        <StatusNotice tone="warning" role="alert" title="Keep this import open">
          <p>
            An import is in progress or its outcome is unknown. Stay to check the result and save a
            sensitive recovery file before leaving.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => blocker.reset()}>Stay and recover</Button>
            <Button variant="destructive" onClick={() => blocker.proceed()}>
              Leave without saved recovery
            </Button>
          </div>
        </StatusNotice>
      )}
      <Stepper
        variant="workflow"
        ariaLabel="Import progress"
        activeStepId={step}
        onStepChange={next => {
          if (c.locked || c.result) return
          if (next === 'choose' || (next === 'review' && c.review)) setStep(next)
        }}
        steps={[
          {
            id: 'choose',
            disabled: c.locked || reading || !!c.result,
            label: 'Choose file',
            content: choose,
            renderFooter: () => null,
          },
          {
            id: 'review',
            disabled: !c.review || c.locked || reading || !!c.result,
            label: 'Review changes',
            content: review,
            renderFooter: () => null,
          },
          {
            id: 'done',
            disabled: !c.result,
            label: 'Complete',
            content: c.result ? <ImportDone controller={c} runHere /> : null,
            renderFooter: () => null,
          },
        ]}
      />
    </WorkflowLayout>
  )
}
export function ImportDone({
  controller: c,
  runHere,
}: {
  controller: ReviewController
  runHere: boolean
}) {
  const result = c.result!
  const showQuery = useQuery({
    queryKey: ['imported-show', result.destinationShowId],
    queryFn: async () =>
      (await liveApiClient.lookupShow({ showId: result.destinationShowId })).show,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    networkMode: 'always',
    retry: false,
  })
  const ownership = useOwnership(result.destinationShowId)
  const ownershipStatus = ownership.isError ? undefined : ownership.data
  const show =
    showQuery.isFetchedAfterMount && showQuery.isSuccess && !showQuery.isFetching
      ? showQuery.data
      : undefined
  const { setCurrentShow } = useCurrentShow(),
    navigate = useNavigate()
  const [sole, setSole] = useState(false)
  const [designationUnknown, setDesignationUnknown] = useState(false)
  const designation = useTransferAction(async () => {
    try {
      await transferRequest(
        `shows/${encodeURIComponent(result.destinationShowId)}/ownership/designate`,
        { confirmOnlyActiveCopy: true }
      )
    } catch (error) {
      setDesignationUnknown(true)
      throw error
    } finally {
      await ownership.refetch()
    }
  })
  return (
    <section className="space-y-4">
      <WorkflowHeading description="Your show and its records are now available on this server.">
        Import complete
      </WorkflowHeading>
      <PackageSummary value={c.packageValue} />
      <StatusNotice tone="success" role="status">
        {result.alreadyApplied ? 'Previously completed import recovered.' : 'Import completed.'}
        {'outcome' in result.receipt && result.receipt.outcome === 'unchanged' && (
          <p>No changes were needed. This show already matches the reviewed import.</p>
        )}
      </StatusNotice>
      <p>
        Show status: {show?.status ?? 'Status unavailable'}.{' '}
        {ownershipStatus
          ? `Editing: ${ownershipStatus.canOperate ? 'enabled on this server' : 'read-only'}.`
          : 'Editing status unavailable.'}
      </p>
      <p>Importing does not publish results or reopen a finalized show.</p>
      {showQuery.isFetching && <p role="status">Loading show details…</p>}
      {!show && (
        <Button
          variant="outline"
          disabled={showQuery.isFetching}
          onClick={() => void showQuery.refetch()}
        >
          Refresh show details
        </Button>
      )}
      {(ownership.isError || !ownershipStatus || designationUnknown) && (
        <div>
          <p>
            Check editing status before taking an ownership action. The import is complete; do not
            import again.
          </p>
          <Button
            variant="outline"
            onClick={() =>
              void ownership.refetch().then(result => {
                if (!result.isError) setDesignationUnknown(false)
              })
            }
          >
            Refresh editing status
          </Button>
        </div>
      )}
      <WorkflowFooter
        secondary={
          <Button
            variant="outline"
            onClick={() => downloadJson(result.receipt, 'import-receipt.json')}
          >
            Save import receipt
          </Button>
        }
      >
        <Button
          disabled={!show}
          onClick={() => {
            if (!show) return
            setCurrentShow(show)

            navigate(`/${show.slug}/setup`)
          }}
        >
          Open show
        </Button>
      </WorkflowFooter>
      {runHere && (
        <details>
          <summary className="font-medium">Run this show here</summary>
          <div className="mt-4 space-y-4">
            {ownershipStatus &&
              (ownershipStatus.canOperate ? (
                <p>This show is already active on this server.</p>
              ) : ownershipStatus.canDesignate ? (
                <div className="space-y-3">
                  <StatusNotice tone="warning" title="Confirm this is the only active copy">
                    Activation enables editing on this server. If another server runs this show, use
                    a signed handover instead.
                  </StatusNotice>
                  <Check checked={sole} onChange={setSole}>
                    I confirm this is the only active copy of this show.
                  </Check>
                  <Button
                    disabled={
                      !sole || designation.isPending || designationUnknown || ownership.isFetching
                    }
                    onClick={() => designation.mutate()}
                  >
                    Designate this copy active
                  </Button>
                  {designation.isError && (
                    <StatusNotice tone="warning" role="alert">
                      Unable to confirm designation. Refresh editing status before retrying.
                    </StatusNotice>
                  )}
                </div>
              ) : (
                <div>
                  <p>
                    Next: use a signed handover to move editing responsibility from the active
                    server. Verify installation identities, synchronize the source, then prepare and
                    review the signed transfer.
                  </p>
                  <Link
                    className="underline"
                    to={`/admin/show-transfer?task=move&showId=${encodeURIComponent(result.destinationShowId)}`}
                  >
                    Continue to signed handover
                  </Link>
                </div>
              ))}
            {!ownershipStatus?.canOperate && (
              <p>Open show uses setup until this copy is active on this server.</p>
            )}
            {ownershipStatus?.canOperate && (
              <Button
                disabled={!show || ownership.isFetching || designationUnknown}
                onClick={() => {
                  if (!show) return
                  setCurrentShow(show)

                  navigate(show.slug ? `/${show.slug}/run` : '/run')
                }}
              >
                Run show
              </Button>
            )}
          </div>
        </details>
      )}
    </section>
  )
}
